"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MEDIA_GENERATOR_VENDOR_LABEL = exports.MEDIA_MODELS = exports.REFERENCE_ROLE_IDS = exports.ASPECT_RATIOS = void 0;
exports.ratioParts = ratioParts;
exports.randomSeed = randomSeed;
exports.maxReferencesFor = maxReferencesFor;
exports.modelsFor = modelsFor;
exports.defaultModelFor = defaultModelFor;
exports.findModel = findModel;
exports.generatorKeyFor = generatorKeyFor;
exports.modelDisplayLabel = modelDisplayLabel;
exports.ratiosFor = ratiosFor;
exports.ratioTokenFor = ratioTokenFor;
exports.solveDimensions = solveDimensions;
const enums_1 = require("./enums");
/**
 * Aspect ratios offered in the Generate modal, in display order.
 *
 * `2:1` and `1:2` were missing, which made Magnific's `horizontal_2_1` / `vertical_1_2` unreachable
 * on every model that lists them — a capability the provider has and we simply never exposed.
 */
exports.ASPECT_RATIOS = [
    'auto',
    '1:1',
    '3:2',
    '2:3',
    '3:4',
    '4:3',
    '4:5',
    '5:4',
    '9:16',
    '16:9',
    '2:1',
    '1:2',
    '21:9',
];
/** Numeric width/height for a ratio. `auto` has none — the model decides. */
function ratioParts(ratio) {
    if (ratio === 'auto')
        return null;
    const [w, h] = ratio.split(':').map(Number);
    return Number.isFinite(w) && Number.isFinite(h) && h > 0 ? { w, h } : null;
}
/** Runtime list, so the API can validate an incoming role rather than trusting the type. */
exports.REFERENCE_ROLE_IDS = ['structure', 'style'];
/**
 * A seed inside the model's own accepted range.
 *
 * Regenerate means "same inputs, new seed", so the value only has to differ from last time — but it
 * DOES have to be legal, and the bounds are not the same everywhere. Deriving one from the clock and
 * hoping (the previous approach) produces 0 on models whose floor is 1, and overflows models whose
 * ceiling is 2^31-1, in both cases as a provider-side rejection rather than anything readable.
 */
function randomSeed(range) {
    const span = Math.max(1, range.max - range.min + 1);
    return range.min + Math.floor(Math.random() * span);
}
/** Convenience: how many reference images this model accepts in total. */
function maxReferencesFor(caps) {
    switch (caps.references.kind) {
        case 'none':
            return 0;
        case 'list':
            return caps.references.max;
        case 'roles':
            return caps.references.roles.length;
    }
}
/**
 * Geometry for a model that takes a NAMED aspect ratio and no pixels.
 *
 * The numbers never reach the provider — the generator solves a width/height only to pick the
 * nearest named ratio from it, then throws the pixels away. Kept permissive so the solver never
 * clamps a ratio into a different shape on the way out; a tight bound here would silently change
 * which enum value gets chosen.
 */
const MAGNIFIC_RATIO_PIXELS = {
    multipleOf: 8,
    minSide: 512,
    maxSide: 2048,
    maxPixels: 4_194_304,
};
/**
 * Magnific's ten named ratios — flux-dev, flux-pro, hyperflux and the Kontext family.
 *
 * `auto` maps to the square token deliberately: these endpoints have no "let the model decide"
 * value, so the honest translation of "no preference" is their own documented default.
 */
const MAGNIFIC_TEN_RATIOS = {
    auto: 'square_1_1',
    '1:1': 'square_1_1',
    '4:3': 'classic_4_3',
    '3:4': 'traditional_3_4',
    '16:9': 'widescreen_16_9',
    '9:16': 'social_story_9_16',
    '3:2': 'standard_3_2',
    '2:3': 'portrait_2_3',
    '2:1': 'horizontal_2_1',
    '1:2': 'vertical_1_2',
    '4:5': 'social_post_4_5',
};
/**
 * Seedream's eight. Neither a subset nor a superset of the ten: it drops 4:5, 2:1 and 1:2, and it
 * is the only Magnific family with a 21:9.
 */
const SEEDREAM_RATIOS = {
    auto: 'square_1_1',
    '1:1': 'square_1_1',
    '4:3': 'classic_4_3',
    '3:4': 'traditional_3_4',
    '16:9': 'widescreen_16_9',
    '9:16': 'social_story_9_16',
    '3:2': 'standard_3_2',
    '2:3': 'portrait_2_3',
    '21:9': 'cinematic_21_9',
};
/** Nano Banana takes the ratio verbatim, so every token is its own key. */
const NANO_BANANA_RATIOS = {
    auto: '1:1',
    '1:1': '1:1',
    '2:3': '2:3',
    '3:2': '3:2',
    '4:3': '4:3',
    '3:4': '3:4',
    '5:4': '5:4',
    '4:5': '4:5',
    '16:9': '16:9',
    '9:16': '9:16',
    '21:9': '21:9',
};
/** Azure FLUX geometry: multiples of 32, and a hard 4 MP ceiling. */
const FLUX_PIXELS = {
    multipleOf: 32,
    minSide: 256,
    maxSide: 2496,
    maxPixels: 4_194_304,
};
/**
 * Mystic's thirteen, minus what we cannot honestly send.
 *
 * OMITTED on purpose:
 *  - `smartphone_horizontal_20_9` / `smartphone_vertical_9_20` — 20:9 and 9:20 have no entry in our
 *    shared ratio list, and inventing an approximation is the substitution this map exists to stop.
 *  - 4:5. The published schema prints its token as `social_post_4_5'` — WITH a trailing apostrophe —
 *    and which spelling the API actually validates could not be established. Offering it means
 *    guessing between a value that is silently ignored and one that is rejected, so it is left out
 *    until someone can confirm against a live call. One ratio on one model is the cheap side of that
 *    trade.
 */
const MYSTIC_RATIOS = {
    auto: 'square_1_1',
    '1:1': 'square_1_1',
    '4:3': 'classic_4_3',
    '3:4': 'traditional_3_4',
    '16:9': 'widescreen_16_9',
    '9:16': 'social_story_9_16',
    '3:2': 'standard_3_2',
    '2:3': 'portrait_2_3',
    '2:1': 'horizontal_2_1',
    '1:2': 'vertical_1_2',
    '5:4': 'social_5_4',
};
/**
 * `fluid` accepts only FIVE ratios — the one place a Mystic variant differs from its siblings, and
 * the reason each variant is registered separately rather than as one entry with a style dropdown.
 */
const MYSTIC_FLUID_RATIOS = {
    auto: 'square_1_1',
    '1:1': 'square_1_1',
    '4:3': 'classic_4_3',
    '3:4': 'traditional_3_4',
    '16:9': 'widescreen_16_9',
    '9:16': 'social_story_9_16',
};
/** The two named slots every Mystic variant exposes. */
const MYSTIC_ROLES = [
    { id: 'structure', label: 'Structure', hint: 'Composition and layout are taken from this image.' },
    { id: 'style', label: 'Style', hint: 'Colour, texture and overall look come from here.' },
];
/**
 * One Mystic style variant.
 *
 * Six entries sharing a single endpoint: the variant is chosen by a `model` field in the BODY, not
 * by the path, so the generator maps our id to {path, bodyModel}. They are separate registry
 * entries rather than one entry plus a style dropdown because `fluid` genuinely offers fewer
 * aspect ratios — a single entry would have to advertise the union and then silently drop shapes.
 */
function mysticVariant(id, label, hint, ratioTokens = MYSTIC_RATIOS) {
    return {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id,
        label,
        hint,
        caps: {
            /**
             * ROLES, not a list. `structure_reference` and `style_reference` are separate single-image
             * base64 fields that mean different things — one drives composition, the other appearance. A
             * flat "2 references" would let them be swapped silently, producing a plausible wrong image.
             */
            references: { kind: 'roles', roles: MYSTIC_ROLES },
            // Size is the `resolution` tier below, not megapixels — the endpoint takes no pixel fields.
            megapixels: [1],
            ratioTokens,
            canRefine: false,
            // No seed field of any kind — determinism is `fixed_generation: boolean` instead.
            seed: undefined,
            pixels: MAGNIFIC_RATIO_PIXELS,
            // Lowercase, unlike Nano Banana's uppercase tiers on the same provider.
            resolutions: [
                { value: '1k', label: '1K' },
                { value: '2k', label: '2K' },
                { value: '4k', label: '4K' },
            ],
            // The endpoint's own default. Sending 1k because it happens to be first would produce a
            // SMALLER image than sending nothing at all.
            resolutionDefault: '2k',
        },
    };
}
/**
 * Z-Image. The field is `image_size` and the tokens are PRESET NAMES, each with a fixed pixel size,
 * so this map carries shape AND resolution — there is no other size control and a hard 1024px
 * ceiling on every preset.
 *
 * `square` (512x512) is unreachable: 1:1 maps to `square_hd` (1024x1024). Giving up the smaller
 * square is the right trade — nobody picks 1:1 hoping for fewer pixels.
 */
const Z_IMAGE_PRESETS = {
    auto: 'square_hd',
    '1:1': 'square_hd',
    '3:4': 'portrait_3_4',
    '9:16': 'portrait_9_16',
    '4:3': 'landscape_4_3',
    '16:9': 'landscape_16_9',
};
/**
 * RunWay. The field is `ratio`, NOT `aspect_ratio`, and it is REQUIRED — the only required size
 * field in the catalogue, and so the one place where omitting an unmapped token means a provider
 * 400 rather than a graceful fallback to a default. The generator rejects an unsupported ratio
 * before the request leaves.
 *
 * The tokens are OPAQUE pixel pairs: never reduce them or compute with them. Only pairs that are
 * EXACTLY our ratio are used — 1680:720 is exactly 21:9, while 2112:912 (2.3158) and 1808:768
 * (2.3542) are not, so neither appears here.
 */
const RUNWAY_RATIO_PAIRS = {
    auto: '1024:1024',
    '1:1': '1024:1024',
    '4:3': '1440:1080',
    '3:4': '1080:1440',
    '16:9': '1920:1080',
    '9:16': '1080:1920',
    '21:9': '1680:720',
};
/**
 * The models we can honestly offer.
 *
 * Deliberately short. The temptation is to list everything a provider publishes, but a dropdown
 * entry that errors is worse than no entry — and three of our six generators cannot route an
 * arbitrary id anyway. Entries are added when the generator can actually send them.
 */
exports.MEDIA_MODELS = [
    {
        generator: 'azure',
        // The Azure deployment name this generator sends, in the URL path and the body's `model`. App
        // Settings → AI models offers only a FLUX.2-pro deployment by this name (the only one verified on
        // the route); an AZURE_OPENAI_DEPLOYMENT_IMAGE naming another is flagged on that card. So these
        // caps hold.
        id: 'flux-2-pro',
        label: 'FLUX.2 [pro]',
        hint: 'Included in your plan.',
        caps: {
            /**
             * FLUX.2 [pro] accepts eight (`input_image` … `input_image_8`; a ninth is a hard 422). We
             * offer five, because BFL also documents a ~9 MP COMBINED input+output budget — references
             * are metered at a whole megapixel each regardless of their real size — so eight is only
             * reachable at 1 MP output. Five leaves room for a 2 MP result. Microsoft's Azure docs never
             * mention the combined budget, so treating it as binding here is deliberate caution.
             */
            references: { kind: 'list', max: 5 },
            // No 4 MP tier: the model's own ceiling IS 4 MP, so a 4 MP request leaves no headroom once
            // sides snap to multiples of 32, and 3840×2160 is impossible outright.
            megapixels: [1, 2],
            canRefine: true,
            /**
             * UNVERIFIED bounds — Azure documents that `seed` is accepted but not its range, so this is
             * the conservative signed-32-bit window we were already sending, not a documented fact.
             *
             * Worth knowing: a live probe showed the same body with the SAME seed returns different bytes
             * on this deployment, so the seed varies output without making it reproducible. Never key a
             * cache or a golden-image test on it.
             */
            seed: { min: 0, max: 2_147_483_647 },
            pixels: FLUX_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.OPENAI,
        id: 'gpt-image-2',
        label: 'GPT Image 2',
        hint: 'Runs on your connected OpenAI key.',
        caps: {
            references: { kind: 'list', max: 5 },
            megapixels: [1, 2, 4],
            canRefine: true,
            // OpenAI's image API exposes no seed — Regenerate varies by nature, not by parameter.
            seed: undefined,
            // Documented gpt-image-2 geometry, verified against the current API reference: both edges
            // multiples of 16, longest edge 3840, ratio within 1:3–3:1, total pixels 655,360–8,294,400.
            // maxSide was 4096 here, which the solver could actually emit at wide ratios — every such
            // request would have been rejected outright. The same flexible rule is documented on the
            // EDIT method too, so a reference-image generation can use these sizes as well.
            pixels: { multipleOf: 16, minSide: 512, maxSide: 3840, maxPixels: 8_294_400, minPixels: 655_360 },
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        /**
         * On the `/text-to-image/*` endpoints the model is chosen by URL PATH — there is no `model` body
         * parameter. (Mystic is the one exception: it lives at `/v1/ai/mystic` and takes a `model` enum
         * in the body.) So this id is a label the generator does not read, and a SECOND entry would be a
         * lie today: it would return flux-dev output under a different name.
         *
         * There are ~27 image endpoints, and flux-dev is one of the weaker ones — no image input, no
         * pixel control. Registering more means teaching the generator to route the path AND to encode
         * the model into the task id, because the status path is `<POST path>/{task-id}` and `fetchTask`
         * receives no model (fal.generator.ts solves the same problem the same way). Until then, one.
         */
        id: 'flux-dev',
        label: 'Magnific · Flux Dev',
        hint: 'Runs on the connected Magnific key. Sizes are approximate — Magnific takes a named aspect ratio, not exact pixels.',
        caps: {
            // The text-to-image endpoint takes a prompt and a ratio. No image input of any kind:
            // the schema has exactly five properties and none of them accepts an image.
            references: { kind: 'none' },
            /**
             * One tier, and it is nominal. There is no width/height parameter — the endpoint decides its
             * own resolution from the ratio — so the megapixel control cannot influence the result. It
             * stays present because the DTO requires a non-empty subset of {1,2,4}, and 1 is the only
             * value that does not imply a size we cannot deliver.
             */
            megapixels: [1],
            // Magnific's ten named ratios. 21:9 and 5:4 have no token here, so they are not offered.
            ratioTokens: MAGNIFIC_TEN_RATIOS,
            /**
             * False, and it must stay false. A refinement here means inheriting the prompt AND turning
             * off provider-side prompt rewriting (`enhancePrompt: false`); only azure-flux implements the
             * second half. Claiming otherwise would offer "Request changes" on a generator that quietly
             * rewrites the prompt differently every call, so each version would be an unrelated image.
             */
            canRefine: false,
            /**
             * Documented 1..4294967295 — note the floor of ONE, which is why this is a range and not a
             * boolean. Our DTO allows 0 and the modal derives a seed from the clock, so 0 is reachable
             * and would be rejected.
             */
            seed: { min: 1, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'seedream-v4-5',
        label: 'Magnific · Seedream 4.5',
        hint: 'Text to image. For reference images, pick Seedream 4.5 (with references).',
        caps: {
            // The plain endpoint has no image input at all — that lives on the `-edit` sibling below.
            references: { kind: 'none' },
            megapixels: [1],
            // Seedream's own EIGHT — no 4:5, no 2:1/1:2, but it does have cinematic_21_9.
            ratioTokens: SEEDREAM_RATIOS,
            canRefine: false,
            seed: { min: 0, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'seedream-v4-5-edit',
        label: 'Magnific · Seedream 4.5 (with references)',
        hint: 'Needs at least one reference image; up to five. JPG or PNG, at least 256×256.',
        caps: {
            /**
             * `min: 1` is not decoration — `reference_images` is REQUIRED on this endpoint, so without it
             * the request is refused. The API says so before the provider does.
             */
            references: { kind: 'list', max: 5, min: 1 },
            megapixels: [1],
            ratioTokens: SEEDREAM_RATIOS,
            /**
             * True in the sense refinement is implemented: the prompt carries the requested change and
             * the chosen references are re-sent unchanged.
             *
             * NOT "edit the image you are looking at" — `startTransient` never feeds a parent's OUTPUT
             * back in as a reference, so a refinement here re-runs the edit with a new instruction rather
             * than editing the previous result. Doing that properly is worth having and is a change to
             * the refinement path, not to this flag.
             */
            canRefine: true,
            seed: { min: 0, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'flux-2-pro',
        label: 'Magnific · FLUX.2 [pro]',
        hint: 'Takes exact pixel sizes and up to 4 reference images.',
        caps: {
            // `input_image` … `input_image_4`, base64. NOT `input_image_1` — that field does not exist.
            references: { kind: 'list', max: 4 },
            /**
             * One tier. The documented ceiling is 1440 PER SIDE, so the largest square is 1440² ≈ 2.07 MP
             * and a 2 MP tier is only reachable near-square — offering it would fail at wide ratios.
             */
            megapixels: [1],
            canRefine: true,
            seed: { min: 0, max: 4_294_967_295 },
            /**
             * Real pixels, sent as flat `width`/`height`. `multipleOf: 8` is UNVERIFIED — the docs give
             * only min and max — so it is a defensive choice, not a documented one.
             */
            pixels: { multipleOf: 8, minSide: 256, maxSide: 1440, maxPixels: 1440 * 1440 },
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'flux-2-turbo',
        label: 'Magnific · FLUX.2 [turbo]',
        hint: 'Faster, larger output. No reference images.',
        caps: {
            // Verified absent: the request schema has seven properties and no image input under any name.
            references: { kind: 'none' },
            // 2048² is 4.19 MP, so 1, 2 and 4 MP are all genuinely reachable here.
            megapixels: [1, 2, 4],
            canRefine: false,
            seed: { min: 0, max: 4_294_967_295 },
            /**
             * Nested `image_size: { width, height }` — a DIFFERENT dialect from its flux-2-pro sibling,
             * which is flat. `multipleOf: 8` is again defensive rather than documented.
             */
            pixels: { multipleOf: 8, minSide: 512, maxSide: 2048, maxPixels: 2048 * 2048 },
        },
    },
    mysticVariant('mystic-realism', 'Magnific · Mystic (Realism)', 'Photoreal. The default style.'),
    mysticVariant('mystic-fluid', 'Magnific · Mystic (Fluid)', 'Follows the prompt most literally. Fewer aspect ratios than the other styles.', MYSTIC_FLUID_RATIOS),
    mysticVariant('mystic-zen', 'Magnific · Mystic (Zen)', 'Cleaner, smoother output.'),
    mysticVariant('mystic-flexible', 'Magnific · Mystic (Flexible)', 'Looser interpretation of the prompt.'),
    mysticVariant('mystic-super-real', 'Magnific · Mystic (Super Real)', 'Pushes hardest toward photographic detail.'),
    mysticVariant('mystic-editorial-portraits', 'Magnific · Mystic (Editorial Portraits)', 'Tuned for people and portrait work.'),
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'nano-banana-pro',
        label: 'Magnific · Nano Banana Pro',
        hint: 'Up to 14 reference images, each with its own role in the prompt.',
        caps: {
            /**
             * Fourteen — by far the widest reference support of anything registered. The generator sends
             * them as `{ image, mime_type }` objects pointing at short-lived SAS URLs, because this
             * endpoint fetches references itself and takes no inline bytes.
             */
            references: { kind: 'list', max: 14 },
            megapixels: [1],
            // BARE ratio strings on the wire — the token is the ratio itself.
            ratioTokens: NANO_BANANA_RATIOS,
            canRefine: true,
            // No seed, no fixed_generation, no guidance — nothing to make a run reproducible.
            seed: undefined,
            pixels: MAGNIFIC_RATIO_PIXELS,
            /**
             * UPPERCASE K, unlike Mystic's lowercase `1k` — the same provider, different casing per
             * endpoint. These go on the wire verbatim; normalising the case anywhere in this path is a
             * silent downgrade, since an unrecognised tier is ignored rather than rejected.
             */
            resolutions: [
                { value: '1K', label: '1K' },
                { value: '2K', label: '2K' },
                { value: '4K', label: '4K' },
            ],
            resolutionDefault: '2K',
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'flux-kontext-pro',
        label: 'Magnific · FLUX Kontext [pro]',
        hint: 'Image editing — needs one reference image to work from.',
        caps: {
            /**
             * REQUIRED, despite the docs reading otherwise.
             *
             * The schema does not list `input_image` in a `required` array, which is why this shipped as
             * optional — but these Magnific schemas frequently have NO `required` array at all (flux-dev's
             * omits it entirely while plainly needing a prompt), so absence there proves nothing. A live
             * call with no reference returns a validation error, and Kontext is BFL's image-EDITING
             * family, so needing an input is what the model IS.
             *
             * The reference is a URL — `input_image` is `format: uri` here where the FLUX 2 family uses
             * the same field name for base64 — so the generator marks this model URL-only and mints a SAS.
             */
            references: { kind: 'list', max: 1, min: 1 },
            megapixels: [1],
            ratioTokens: MAGNIFIC_TEN_RATIOS,
            canRefine: true,
            // Documented as an integer with NO bounds, so this is the conservative signed-32-bit window
            // rather than a documented fact. UNVERIFIED.
            seed: { min: 1, max: 2_147_483_647 },
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'flux-kontext-max',
        label: 'Magnific · FLUX Kontext [max]',
        hint: 'Image editing — needs at least one reference image, up to four.',
        caps: {
            // REQUIRED here, unlike Pro: this endpoint edits an image rather than guiding a generation.
            references: { kind: 'list', max: 4, min: 1 },
            megapixels: [1],
            ratioTokens: MAGNIFIC_TEN_RATIOS,
            canRefine: true,
            seed: { min: 1, max: 2_147_483_647 },
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'flux-2-klein',
        label: 'Magnific · FLUX.2 [klein]',
        hint: 'Fast, with up to 4 reference images.',
        caps: {
            references: { kind: 'list', max: 4 },
            megapixels: [1],
            ratioTokens: MAGNIFIC_TEN_RATIOS,
            canRefine: true,
            seed: { min: 0, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
            // Named ratios AND a tier — the two are orthogonal on this endpoint.
            resolutions: [
                { value: '1k', label: '1K' },
                { value: '2k', label: '2K' },
            ],
            resolutionDefault: '1k',
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'flux-2-flex',
        label: 'Magnific · FLUX.2 [flex]',
        hint: 'Exact pixel sizes up to 1920 per side, with up to 4 reference images.',
        caps: {
            references: { kind: 'list', max: 4 },
            // 1920² is 3.7 MP, so 1 and 2 MP are reachable; 4 MP is not at any usable ratio.
            megapixels: [1, 2],
            canRefine: true,
            seed: { min: 0, max: 4_294_967_295 },
            // Flat width/height like its Pro sibling — NOT the nested image_size Turbo uses.
            pixels: { multipleOf: 16, minSide: 256, maxSide: 1920, maxPixels: 1920 * 1920 },
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'flux-pro-v1-1',
        label: 'Magnific · FLUX Pro 1.1',
        hint: 'Text to image. No reference images.',
        caps: {
            references: { kind: 'none' },
            megapixels: [1],
            ratioTokens: MAGNIFIC_TEN_RATIOS,
            canRefine: false,
            // Integer with no documented bounds — conservative window, UNVERIFIED.
            seed: { min: 1, max: 2_147_483_647 },
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'seedream-v5-pro',
        label: 'Magnific · Seedream 5 Pro',
        hint: 'Text to image. For reference images, pick Seedream 5 Pro (with references).',
        caps: {
            references: { kind: 'none' },
            megapixels: [1],
            ratioTokens: SEEDREAM_RATIOS,
            canRefine: false,
            seed: { min: 0, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
            resolutions: [
                { value: '1.5k', label: '1.5K' },
                { value: '2k', label: '2K' },
            ],
            resolutionDefault: '2k',
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'seedream-v5-pro-edit',
        label: 'Magnific · Seedream 5 Pro (with references)',
        hint: 'Needs at least one reference image. JPG or PNG.',
        caps: {
            /**
             * The endpoint documents up to TEN. Capped at 5 here to match the request DTO's array limit —
             * advertising ten while the API rejects the sixth is the mismatch this registry exists to
             * prevent. Raise both together.
             */
            references: { kind: 'list', max: 5, min: 1 },
            megapixels: [1],
            ratioTokens: SEEDREAM_RATIOS,
            canRefine: true,
            seed: { min: 0, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
            resolutions: [
                { value: '1.5k', label: '1.5K' },
                { value: '2k', label: '2K' },
            ],
            resolutionDefault: '2k',
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'seedream-v5-lite',
        label: 'Magnific · Seedream 5 Lite',
        hint: 'Text to image. No resolution tier on this one.',
        caps: {
            references: { kind: 'none' },
            megapixels: [1],
            ratioTokens: SEEDREAM_RATIOS,
            canRefine: false,
            seed: { min: 0, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
            // Deliberately absent: Lite has no `resolution` field, unlike Pro.
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'seedream-v5-lite-edit',
        label: 'Magnific · Seedream 5 Lite (with references)',
        hint: 'Needs at least one reference image, up to five.',
        caps: {
            references: { kind: 'list', max: 5, min: 1 },
            megapixels: [1],
            ratioTokens: SEEDREAM_RATIOS,
            canRefine: true,
            seed: { min: 0, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'gemini-2-5-flash-image-preview',
        label: 'Magnific · Gemini 2.5 Flash Image',
        hint: 'Chooses its own framing — this model takes no size or aspect ratio at all.',
        caps: {
            // Base64 or URL, up to three.
            references: { kind: 'list', max: 3 },
            megapixels: [1],
            /**
             * EMPTY on purpose. This endpoint has no size field of any kind — no aspect_ratio, no
             * width/height, no resolution — so there is no ratio we could honestly offer. An empty map
             * means the picker greys every option rather than pretending one of them does something.
             */
            ratioTokens: {},
            canRefine: true,
            // No seed field.
            seed: undefined,
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'nano-banana-pro-flash',
        label: 'Magnific · Nano Banana Pro Flash',
        hint: 'Faster Nano Banana. Up to 14 reference images.',
        caps: {
            references: { kind: 'list', max: 14 },
            megapixels: [1],
            ratioTokens: NANO_BANANA_RATIOS,
            canRefine: true,
            seed: undefined,
            pixels: MAGNIFIC_RATIO_PIXELS,
            resolutions: [
                { value: '1K', label: '1K' },
                { value: '2K', label: '2K' },
                { value: '4K', label: '4K' },
            ],
            // 1K here, against Pro's 2K — same family, different documented default.
            resolutionDefault: '1K',
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'runway',
        label: 'Magnific · RunWay',
        hint: 'Fixed output sizes. Prompts are capped at 1000 characters.',
        caps: {
            references: { kind: 'none' },
            megapixels: [1],
            /**
             * Pixel-pair tokens under a `ratio` field, and that field is REQUIRED — the only endpoint in
             * the catalogue where an unmapped ratio cannot simply be omitted. The generator rejects one
             * up front rather than letting the provider 400.
             */
            ratioTokens: RUNWAY_RATIO_PAIRS,
            canRefine: false,
            seed: { min: 0, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
    {
        generator: enums_1.MediaProvider.MAGNIFIC,
        id: 'z-image',
        label: 'Magnific · Z-Image Turbo',
        hint: 'Six fixed sizes, all capped at 1024px.',
        caps: {
            references: { kind: 'none' },
            megapixels: [1],
            // Preset NAMES under `image_size` — the token carries the pixel size, so there is no
            // separate resolution control and 1024px is the ceiling on every one.
            ratioTokens: Z_IMAGE_PRESETS,
            canRefine: false,
            seed: { min: 0, max: 4_294_967_295 },
            pixels: MAGNIFIC_RATIO_PIXELS,
        },
    },
];
/** Models available for a generator, in display order. */
function modelsFor(generator) {
    return exports.MEDIA_MODELS.filter((m) => m.generator === generator);
}
/** A generator's default model — the first registered for it. */
function defaultModelFor(generator) {
    return modelsFor(generator)[0];
}
function findModel(generator, id) {
    if (!id)
        return defaultModelFor(generator);
    return exports.MEDIA_MODELS.find((m) => m.generator === generator && m.id === id);
}
/**
 * Vendor name per generator key. 'azure' is our first-party one, which the Generate modal's
 * dropdown calls "Smorgas Social · FLUX".
 */
exports.MEDIA_GENERATOR_VENDOR_LABEL = {
    azure: 'Smorgas Social',
    [enums_1.MediaProvider.OPENAI]: 'OpenAI',
    [enums_1.MediaProvider.REPLICATE]: 'Replicate',
    [enums_1.MediaProvider.STABILITY]: 'Stability AI',
    [enums_1.MediaProvider.FAL]: 'Fal.ai',
    [enums_1.MediaProvider.MAGNIFIC]: 'Magnific',
};
/**
 * The registry key an asset's provenance maps to, or undefined when that provenance has no models
 * at all (UPLOAD, CANVA, FIGMA, and the retired NANO_BANANA/VEO/FREEPIK).
 *
 * FLUX is the odd one: it is our first-party Azure deployment, which the registry keys as 'azure'.
 */
function generatorKeyFor(generator) {
    if (generator === enums_1.AssetGenerator.FLUX)
        return 'azure';
    return Object.values(enums_1.MediaProvider).includes(generator)
        ? generator
        : undefined;
}
/**
 * "Magnific · Nano Banana Pro" for a stored (generator, model) pair, or null when there is nothing
 * to name.
 *
 * Keyed on the PAIR, never the id alone: 'flux-2-pro' is registered under BOTH 'azure' and Magnific
 * with different labels, so an id-only lookup would credit a Magnific image to our own model.
 *
 * Note it never calls `findModel` with an empty id — that returns the generator's DEFAULT model,
 * which would print a confident, invented provenance on every legacy row.
 */
function modelDisplayLabel(generator, modelId) {
    const id = modelId?.trim();
    if (!id)
        return null;
    const key = generatorKeyFor(generator);
    const vendor = key ? exports.MEDIA_GENERATOR_VENDOR_LABEL[key] : undefined;
    const label = key ? findModel(key, id)?.label : undefined;
    // Unregistered or retired id (we removed the Imagen 4 entries; Replicate/Stability/Fal have no
    // entries at all). The raw id is the only provenance that row has — better than hiding it, and
    // far better than snapping it to a neighbouring registry entry.
    if (!label)
        return vendor ? `${vendor} · ${id}` : id;
    // Magnific's own labels already carry the vendor; azure's and OpenAI's do not.
    return vendor && !label.startsWith(vendor) ? `${vendor} · ${label}` : label;
}
/** Ratios a model offers — its own list, or everything when unrestricted. */
function ratiosFor(model) {
    // The token map's keys ARE the offered list where one exists — one source, so the ratios shown
    // and the ratios we can actually send cannot drift apart.
    const tokens = model.caps.ratioTokens;
    if (tokens)
        return exports.ASPECT_RATIOS.filter((r) => tokens[r] !== undefined);
    return model.caps.aspectRatios ?? [...exports.ASPECT_RATIOS];
}
/**
 * The token this model puts on the wire for a ratio, or undefined when it can't produce it.
 *
 * The generator calls this instead of reconstructing a ratio from the solved pixels. That
 * reconstruction was a nearest-match over a per-model table, which is exactly how a ratio the model
 * does not support became a different shape it does — silently, because these providers ignore what
 * they don't recognise rather than rejecting it.
 */
function ratioTokenFor(model, ratio) {
    return model.caps.ratioTokens?.[ratio];
}
/**
 * Turn an aspect ratio and a megapixel budget into width/height the provider will accept.
 *
 * This is the piece that has to be right. Providers reject out-of-range geometry outright, and the
 * naive implementations fail in both directions: 1024×576 ("16:9 at 1 MP") is below OpenAI's
 * 655,360-pixel floor, and rounding up at 4 MP for 4:3 lands over the ceiling. So: solve for the
 * budget, snap to the model's multiple, then clamp — re-snapping after every clamp, because a clamp
 * can push a side back off its multiple.
 *
 * `auto` has no ratio, so it falls back to square — the model decides its own framing, and the
 * preview frames square to match.
 */
function solveDimensions(ratio, megapixels, pixels) {
    const parts = ratioParts(ratio) ?? { w: 1, h: 1 };
    const target = Math.max(1, megapixels) * 1_000_000;
    const aspect = parts.w / parts.h;
    // Ideal, unconstrained: w*h = target with w/h = aspect.
    let height = Math.sqrt(target / aspect);
    let width = height * aspect;
    const snap = (v) => Math.max(pixels.minSide, Math.min(pixels.maxSide, Math.round(v / pixels.multipleOf) * pixels.multipleOf));
    width = snap(width);
    height = snap(height);
    // Scale into the pixel envelope. Bounded loop: each pass either fixes it or the snapping has
    // reached a fixed point, and an unbounded loop here would hang a request.
    for (let pass = 0; pass < 6; pass += 1) {
        const area = width * height;
        if (area > pixels.maxPixels) {
            const factor = Math.sqrt(pixels.maxPixels / area);
            const next = { width: snap(width * factor), height: snap(height * factor) };
            if (next.width === width && next.height === height) {
                // Snapping can't get us under; step down one multiple on the longer side.
                if (width >= height)
                    width = Math.max(pixels.minSide, width - pixels.multipleOf);
                else
                    height = Math.max(pixels.minSide, height - pixels.multipleOf);
            }
            else {
                width = next.width;
                height = next.height;
            }
            continue;
        }
        if (pixels.minPixels && area < pixels.minPixels) {
            const factor = Math.sqrt(pixels.minPixels / area);
            const next = { width: snap(width * factor), height: snap(height * factor) };
            if (next.width === width && next.height === height) {
                if (width <= height)
                    width = Math.min(pixels.maxSide, width + pixels.multipleOf);
                else
                    height = Math.min(pixels.maxSide, height + pixels.multipleOf);
            }
            else {
                width = next.width;
                height = next.height;
            }
            continue;
        }
        break;
    }
    return { width, height };
}
//# sourceMappingURL=media-models.js.map